import { FieldComponent, FieldProps } from 'field-rat-core';
import React, { Component } from 'react';
import { arrayMove, SortableContainer, SortableElement } from 'react-sortable-hoc';
import { getSections as getMergedAndRankedSectionDataFromExistingBlock } from '../adminCommunicationSingleton';
import SectionFromInstanceConfig from '../SectionFromInstanceConfig';
import FlatItem from './FlatItem';
import flatItemsToInstanceConfigSections from './flatItemsToInstanceConfigSections';
import sectionsToFlatItems from './sectionsToFlatItems';

type Props = FieldProps<Value>;

interface SortableItem {
    item: FlatItem;
    onDelete: () => void;
}

interface State {
    items: FlatItem[];
}

type Value = SectionFromInstanceConfig[];

export default class QuickShopSectionsAndSkusField
    extends Component<Props, State>
    implements FieldComponent<Value, {}, State> {
    constructor(props: Props) {
        super(props);
        this.handleHeaderCreateClick = this.handleHeaderCreateClick.bind(this);
        this.handleHeaderDeleteClick = this.handleHeaderDeleteClick.bind(this);
        this.state = {
            items: sectionsToFlatItems(getMergedAndRankedSectionDataFromExistingBlock()),
        };
    }

    public async getValue(): Promise<Value> {
        return flatItemsToInstanceConfigSections(this.state.items);
    }

    /**
     * Is called the user drops a drag-drop element
     *
     * @param {any} oldIndex
     * @param {any} newIndex
     */
    public handleSortEnd = ({ oldIndex, newIndex }) => {
        this.setState({
            items: arrayMove(this.state.items, oldIndex, newIndex),
        });
    }

    public handleHeaderCreateClick() {
        const newName = prompt('Enter the new header\'s Name');
        if (newName === null) {
            return; // null means the user clicked "cancel"
        }
        this.setState({
            items: [
                {
                    text: newName,
                    type: 'persistentSectionHeader',
                    value: newName,
                },
                ...this.state.items,
            ],
        });
    }

    public handleHeaderDeleteClick(item: FlatItem) {
        if (!confirm('Delete header "' + item.text + '"?')) {
            return;
        }

        this.setState({
            items: this.state.items.filter((filterItem) => filterItem !== item),
        });
    }

    /**
     * The purpose of this method is to attach the onDelete function to each item and wrap it.
     * This is so onDelete can be used by the JSX inside the SortableElement
     *
     * @param {Array<FlatItem>} items
     * @return {Array<SortableItem>}
     */
    public itemsToSortableItems(items: FlatItem[]): SortableItem[] {
        return items.map((item) => {
            return {
                item,
                onDelete: () => {
                    this.handleHeaderDeleteClick(item);
                },
            };
        });
    }

    public render() {
        return (<div>
            <button type="button"
                className="btn btn-sm btn-default"
                onClick={this.handleHeaderCreateClick}>Create a new header
            </button>
            <div>Use drag and drop to arrange the items and headers below:</div>
            <SortableList
                // @ts-ignore
                items={this.itemsToSortableItems(this.state.items)}
                onSortEnd={this.handleSortEnd} />
        </div>);
    }
}

const SortableItem = SortableElement(({ value }) =>
    (
        <div
            style={{
                backgroundColor: (value.item.type === 'rankedSku' ? '#F8F8F8' : '#d9edf7'),
                border: '1px solid black',
                cursor: 'pointer',
                margin: (value.item.type === 'rankedSku' ? '2px 2em' : '2px 0'),
                userSelect: 'none',
                zIndex: 1000000000,
            }}>
            {value.item.text}
            {value.item.type === 'persistentSectionHeader' &&
                <span>
                    <button type="button"
                        className="btn btn-sm btn-default"
                        style={{ margin: '2px 1em 2px 1em' }}
                        onClick={value.onDelete}>Delete Header</button>
                </span>
            }
            {value.item.type === 'nonPersistentSectionHeader' &&
                <span>
                    &nbsp;(This is a fall-back header for un-categorized SKUs.
                    Please remove all SKUs from it.
                    It will auto-hide if emptied of its SKUs)
            </span>
            }
        </div>
    )
);

interface SortableListProps {
    items: any[];
}

const SortableList = SortableContainer(({ items }: SortableListProps) => {
    return (
        <div style={{ border: '1px solid grey', padding: '3px', margin: '0 3px 0 3px' }}>
            {items.map((value, index) => (
                // @ts-ignore*
                <SortableItem key={`item-${index}`} index={index} value={value} />
            ))}
        </div>
    );
});
